import React, { useEffect, useState } from 'react';
import { getDashboard, getRendements, getPartsCategories, getPartenairesStats } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const COLORS = ['#2196F3', '#FF9800', '#4CAF50', '#f44336', '#9C27B0', '#009688', '#795548', '#607D8F', '#E91E63', '#3F51B5'];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [rendements, setRendements] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [partenairesStats, setPartenairesStats] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const annee = new Date().getFullYear();

  const loadAllData = () => {
    setRefreshing(true);
    Promise.all([
      getDashboard().then(r => setStats(r.data)),
      getRendements(annee).then(r => setRendements(r.data)),
      getPartsCategories().then(r => setCategories(r.data)),
      getPartenairesStats().then(r => setPartenairesStats(r.data)),
    ]).finally(() => setRefreshing(false));
  };

  useEffect(() => { loadAllData(); }, [annee]);

  const fmt = (v: number) => {
    if (!v) return '0';
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return Math.round(v / 1000) + 'K';
    return v.toLocaleString('fr-FR');
  };

  const tauxGlobal = stats?.montantFacture > 0
    ? Math.round((stats.montantPaye / stats.montantFacture) * 100 * 10) / 10 : 0;

  const totalAbonnesCategories = categories.reduce((s: number, c: any) => s + (c.total || 0), 0);

  // Top 5 categories
  const top5Categories = [...categories].sort((a, b) => b.total - a.total).slice(0, 5);

  // Partenaires sorted by montantPaye desc
  const topPartenaires = [...partenairesStats].sort((a, b) => (b.montantPaye || 0) - (a.montantPaye || 0));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Tableau de bord</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#888', fontSize: 14 }}>Annee {annee}</span>
          <button
            onClick={loadAllData}
            disabled={refreshing}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: refreshing ? '#bdbdbd' : '#1976d2', color: '#fff',
              cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>&#8635;</span>
            {refreshing ? 'Actualisation...' : 'Actualiser'}
          </button>
        </div>
      </div>

      {/* Row 1: Main KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #2196F3' }}>
          <div className="kpi-v2-label">Partenaires</div>
          <div className="kpi-v2-value" style={{ color: '#2196F3' }}>{stats?.partenaires || 0}</div>
          <div className="kpi-v2-sub">{stats?.forages || 0} forages &middot; {stats?.secteurs || 0} secteurs</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #FF9800' }}>
          <div className="kpi-v2-label">Abonnes</div>
          <div className="kpi-v2-value" style={{ color: '#FF9800' }}>{stats?.abonnes?.toLocaleString('fr-FR') || 0}</div>
          <div className="kpi-v2-sub">{top5Categories.length > 0 ? top5Categories[0].libelle + ' (' + top5Categories[0].total + ')' : ''}</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #4CAF50' }}>
          <div className="kpi-v2-label">Factures</div>
          <div className="kpi-v2-value" style={{ color: '#4CAF50' }}>{stats?.factures?.toLocaleString('fr-FR') || 0}</div>
          <div className="kpi-v2-sub">Montant: {fmt(stats?.montantFacture || 0)} FCFA</div>
        </div>
        <div className="kpi-card-v2" style={{ borderLeft: '4px solid #9C27B0' }}>
          <div className="kpi-v2-label">Taux de recouvrement</div>
          <div className="kpi-v2-value" style={{ color: tauxGlobal >= 50 ? '#4CAF50' : tauxGlobal >= 25 ? '#FF9800' : '#f44336' }}>{tauxGlobal}%</div>
          <div className="kpi-v2-sub">Recouvre: {fmt(stats?.montantPaye || 0)} FCFA</div>
        </div>
      </div>

      {/* Row 2: Financial summary bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div className="finance-card">
          <div className="finance-icon" style={{ background: '#e3f2fd', color: '#1565C0' }}>&#128176;</div>
          <div>
            <div className="finance-label">Montant facture</div>
            <div className="finance-value">{fmt(stats?.montantFacture || 0)} FCFA</div>
          </div>
        </div>
        <div className="finance-card">
          <div className="finance-icon" style={{ background: '#e8f5e9', color: '#2e7d32' }}>&#9989;</div>
          <div>
            <div className="finance-label">Montant recouvre</div>
            <div className="finance-value" style={{ color: '#2e7d32' }}>{fmt(stats?.montantPaye || 0)} FCFA</div>
          </div>
        </div>
        <div className="finance-card">
          <div className="finance-icon" style={{ background: '#ffebee', color: '#c62828' }}>&#9888;</div>
          <div>
            <div className="finance-label">Reste a recouvrer</div>
            <div className="finance-value" style={{ color: '#c62828' }}>{fmt(stats?.montantRestant || 0)} FCFA</div>
          </div>
        </div>
      </div>

      {/* Row 3: Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Rendement mensuel - double bar */}
        <div className="chart-card">
          <h3>Facturation et recouvrement mensuel ({annee})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rendements} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" angle={-45} textAnchor="end" height={70} fontSize={11} tick={{ fill: '#666' }} />
              <YAxis fontSize={11} tick={{ fill: '#666' }} tickFormatter={(v: any) => fmt(Number(v))} />
              <Tooltip
                formatter={(v: any, name: any) => [Number(v).toLocaleString('fr-FR') + ' FCFA', name === 'montantFacture' ? 'Facture' : 'Recouvre']}
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              />
              <Bar dataKey="montantFacture" name="Facture" fill="#2196F3" radius={[3, 3, 0, 0]} />
              <Bar dataKey="montantPaye" name="Recouvre" fill="#4CAF50" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Taux recouvrement line chart */}
        <div className="chart-card">
          <h3>Evolution du taux de recouvrement ({annee})</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={rendements}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" angle={-45} textAnchor="end" height={70} fontSize={11} tick={{ fill: '#666' }} />
              <YAxis fontSize={11} tick={{ fill: '#666' }} domain={[0, 100]} tickFormatter={(v: any) => v + '%'} />
              <Tooltip
                formatter={(v: any) => [v + '%', 'Taux']}
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              />
              <Line type="monotone" dataKey="rendement" stroke="#FF9800" strokeWidth={3} dot={{ fill: '#FF9800', r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 4: Pie + Top partenaires */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Categories pie */}
        <div className="chart-card">
          <h3>Repartition des abonnes par categorie</h3>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ResponsiveContainer width="50%" height={260}>
              <PieChart>
                <Pie data={top5Categories} dataKey="total" nameKey="libelle" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {top5Categories.map((_: any, i: number) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any) => [Number(v).toLocaleString('fr-FR'), 'Abonnes']} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, fontSize: 13 }}>
              {top5Categories.map((c: any, i: number) => {
                const pct = totalAbonnesCategories > 0 ? Math.round((c.total / totalAbonnesCategories) * 100) : 0;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#333' }}>{c.libelle}</div>
                      <div style={{ color: '#888', fontSize: 12 }}>{c.total.toLocaleString('fr-FR')} ({pct}%)</div>
                    </div>
                  </div>
                );
              })}
              {categories.length > 5 && (
                <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>+ {categories.length - 5} autres categories</div>
              )}
            </div>
          </div>
        </div>

        {/* Top partenaires card */}
        <div className="chart-card">
          <h3>Performance des partenaires</h3>
          <div style={{ fontSize: 13 }}>
            {topPartenaires.slice(0, 6).map((p: any, i: number) => {
              const rendement = p.montantFacture > 0 ? Math.round((p.montantPaye / p.montantFacture) * 100) : 0;
              const barWidth = Math.max(rendement, 3);
              return (
                <div key={p.id} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, color: '#333' }}>{p.libelle}</span>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '1px 8px', borderRadius: 10,
                      background: rendement >= 50 ? '#e8f5e9' : rendement >= 25 ? '#fff3e0' : '#ffebee',
                      color: rendement >= 50 ? '#2e7d32' : rendement >= 25 ? '#e65100' : '#c62828'
                    }}>{rendement}%</span>
                  </div>
                  <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{
                      width: barWidth + '%', height: '100%', borderRadius: 4,
                      background: rendement >= 50 ? '#4CAF50' : rendement >= 25 ? '#FF9800' : '#f44336',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, color: '#888', fontSize: 11 }}>
                    <span>{p.nbAbonnes?.toLocaleString('fr-FR')} abonnes</span>
                    <span>{fmt(p.montantPaye || 0)} / {fmt(p.montantFacture || 0)} FCFA</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Row 5: Full table */}
      <div className="data-table-wrapper">
        <h3 style={{ marginBottom: 15, color: '#333' }}>Detail par partenaire</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Partenaire</th>
              <th style={{ textAlign: 'center' }}>Forages</th>
              <th style={{ textAlign: 'center' }}>Secteurs</th>
              <th style={{ textAlign: 'center' }}>Abonnes</th>
              <th style={{ textAlign: 'center' }}>Factures</th>
              <th style={{ textAlign: 'right' }}>Montant facture</th>
              <th style={{ textAlign: 'right' }}>Montant recouvre</th>
              <th style={{ textAlign: 'right' }}>Reste</th>
              <th style={{ textAlign: 'center' }}>Taux</th>
            </tr>
          </thead>
          <tbody>
            {topPartenaires.map((p: any) => {
              const rendement = p.montantFacture > 0 ? Math.round((p.montantPaye / p.montantFacture) * 100) : 0;
              const reste = (p.montantFacture || 0) - (p.montantPaye || 0);
              return (
                <tr key={p.id}>
                  <td><strong>{p.libelle}</strong></td>
                  <td style={{ textAlign: 'center' }}>{p.nbForages}</td>
                  <td style={{ textAlign: 'center' }}>{p.nbSecteurs}</td>
                  <td style={{ textAlign: 'center' }}>{p.nbAbonnes?.toLocaleString('fr-FR')}</td>
                  <td style={{ textAlign: 'center' }}>{p.nbFactures?.toLocaleString('fr-FR')}</td>
                  <td style={{ textAlign: 'right' }}>{(p.montantFacture || 0).toLocaleString('fr-FR')}</td>
                  <td style={{ textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{(p.montantPaye || 0).toLocaleString('fr-FR')}</td>
                  <td style={{ textAlign: 'right', color: reste > 0 ? '#c62828' : '#2e7d32' }}>{reste.toLocaleString('fr-FR')}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
                      background: rendement >= 50 ? '#e8f5e9' : rendement >= 25 ? '#fff3e0' : '#ffebee',
                      color: rendement >= 50 ? '#2e7d32' : rendement >= 25 ? '#e65100' : '#c62828'
                    }}>
                      {rendement}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
